import React, { Component } from "react"

export class ObservableComponent<P = {}, S = {}, SS = any> extends Component<P, S, SS> {
  scrollObserverEl: HTMLElement | null = null
  handleScrolledIntoView: (() => void) | null = null
}

/**
 * scrollObserverHOC is a higher-order component that adds listeners to the window
 * scroll event so that a handler can be called when the rendered WrappedComponent appears
 * on the screen at least at its half height. We use that to track that a component was
 * actually viewed by the user.
 *
 * The WrappedComponent will have to define which of its elements will be used as a redflag
 * that the rendered component was seen. One can pick which is the most important
 * content from the component to be used as a tracking point and add a ref called "scrollObserverEl"
 *
 * The WrappedComponent will also have to define the callback function handleScrolledIntoView
 * that will be called when the ref element enters the screen.
 *
 * @function scrollObserverHOC
 * @param WrappedComponent the component to be augmented by the HOC
 * @ref scrollObserverEl
 * @callback handleScrolledIntoView
 */
export default function scrollObserverHOC<P>(WrappedComponent: typeof ObservableComponent) {
  return class ScrollObserver extends Component<P> {
    instanceRef: ObservableComponent | null = null
    eventTimeout: NodeJS.Timeout | null = null

    state = {
      viewed: false,
    }

    setInstanceRef = (wrappedComponentInstance: ObservableComponent) => {
      this.instanceRef = wrappedComponentInstance
    }

    handleView = (ev: Event) => {
      if (!this.state.viewed && this.isInPartialView()) {
        this.setState({ viewed: true }, () => {
          window.removeEventListener("scroll", this.eventThrottler, {
            passive: true,
          })
          this.instanceRef?.handleScrolledIntoView?.()
        })
      }
      ev.stopPropagation()
    }

    eventThrottler = (ev: Event) => {
      // ignore scroll events if a handleView execution is in the queue
      if (!this.eventTimeout) {
        this.eventTimeout = setTimeout(() => {
          this.eventTimeout = null
          this.handleView(ev)
        }, 66)
      }
    }

    componentDidMount() {
      if (this.isInPartialView()) {
        this.instanceRef?.handleScrolledIntoView?.()
      } else {
        window.addEventListener("scroll", this.eventThrottler, {
          passive: true,
        })
      }
    }

    componentWillUnmount() {
      //Even if we haven't attached a event listener, detaching an event listener that doesn’t exist won’t raise errors.
      window.removeEventListener("scroll", this.eventThrottler, {
        passive: true,
      })
    }

    isInPartialView = () => {
      const el = this.instanceRef?.scrollObserverEl
      if (!el) return false

      const rect = el.getBoundingClientRect()

      let windowHeight =
        window.innerHeight || document.documentElement.clientHeight
      let windowWidth =
        window.innerWidth || document.documentElement.clientWidth

      return (
        rect.left >= 0 &&
        rect.top >= 0 &&
        rect.left + rect.width <= windowWidth &&
        rect.top + (rect.height * 1) / 2 <= windowHeight
      )
    }

    render() {
      return <WrappedComponent {...this.props} ref={this.setInstanceRef} />
    }
  }
}
